import React, { useEffect, useState } from 'react';
import { Activity, AlertCircle, ArrowRight, Check, Clock, Info, Send, ShieldCheck, Users } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../lib/apiError';
import {
  INVITATION_PREVIEW_KEY,
  captureInvitationToken,
  clearPendingInvitation,
  pendingInvitationToken,
  roleLabel,
  useInvitationPreview,
  useRespondToInvitation,
} from '../services/workspaceTeam';
import './invite-page.css';
import { EnsMark } from '../components/common/EnsLoader';

const statusTone = (status: string) => (status === 'accepted' ? 'ok' : status === 'pending' ? 'warn' : 'bad');

type NoteTone = 'info' | 'ok' | 'warn' | 'bad';

const Note: React.FC<{
  tone?: NoteTone;
  lg?: boolean;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ tone = 'info', lg, icon, action, children }) => (
  <div
    className={`inv-note ${tone === 'info' ? '' : tone}${lg ? ' lg' : ''}`}
    role={tone === 'bad' ? 'alert' : 'status'}
  >
    {icon ?? (tone === 'info' || tone === 'ok' ? <Info aria-hidden /> : <AlertCircle aria-hidden />)}
    <span>{children}</span>
    {action}
  </div>
);

const DetailRow: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({
  icon,
  label,
  children,
}) => (
  <div className="inv-row">
    <span className="ic" aria-hidden>
      {icon}
    </span>
    <dt className="k">{label}</dt>
    <dd className="v">{children}</dd>
  </div>
);

/**
 * /invite/#TOKEN — answer a workspace invitation.
 *
 * The token is read from the fragment (never sent to the server by the
 * browser), parked in sessionStorage for the sign-in round-trip, and removed
 * from the address bar with a replace navigation so it is not left in
 * history. Preview, accept and decline all need a signed-in session; only a
 * Google sign-in as the invited address can accept (enforced server-side).
 */
const InvitePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, loading, logout } = useAuth();

  // Capture during the first render, then drop the fragment from the URL
  // (a replace, so no history entry keeps it).
  const [token, setToken] = useState<string | null>(() => {
    captureInvitationToken(location.hash);
    return pendingInvitationToken();
  });
  useEffect(() => {
    if (location.hash) navigate({ pathname: location.pathname, search: location.search, hash: '' }, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);

  const preview = useInvitationPreview(token, Boolean(user));
  const respond = useRespondToInvitation();
  const [outcome, setOutcome] = useState<'accepted' | 'declined' | null>(null);
  const [confirmDecline, setConfirmDecline] = useState(false);

  const forget = () => {
    clearPendingInvitation();
    setToken(null);
    qc.removeQueries({ queryKey: INVITATION_PREVIEW_KEY });
  };

  const previewErr = preview.error instanceof ApiError ? preview.error : null;
  const p = preview.data;
  const closed = p && p.status !== 'pending';

  // A link that can no longer be used is forgotten.
  useEffect(() => {
    if (previewErr?.code === 'INVITATION_NOT_FOUND' || closed) {
      clearPendingInvitation();
    }
  }, [previewErr?.code, closed]);

  const act = async (action: 'accept' | 'decline') => {
    if (!token) return;
    try {
      await respond.mutateAsync({ token, action });
      setOutcome(action === 'accept' ? 'accepted' : 'declined');
      forget();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined;
      if (code === 'INVITATION_NOT_FOUND' || code === 'INVITATION_CLOSED' || code === 'INVITATION_EXPIRED') {
        clearPendingInvitation();
        void preview.refetch();
      }
    }
  };

  const signIn = () =>
    navigate({ pathname: '/login', hash: '#signin' }, { state: { from: '/invite/', reason: 'invitation' } });

  const switchAccount = async () => {
    await logout();
    signIn();
  };

  const respondErr = respond.error instanceof ApiError ? respond.error : respond.error ? (respond.error as Error) : null;
  const wrongEmail = Boolean(user && p && user.email.toLowerCase() !== p.invitee_email.toLowerCase());
  const notGoogle = Boolean(user && user.provider !== 'google');

  const spinner = (label: string) => (
    <div className="inv-spin">
      <span role="progressbar" aria-label={label}>
        <EnsMark size="sm" icon={28} />
      </span>
    </div>
  );

  let body: React.ReactNode;
  if (outcome) {
    body =
      outcome === 'accepted' ? (
        <Note tone="ok" lg icon={<Check aria-hidden />}>
          You joined the workspace. Your membership is recorded; it does not give you access to the workspace's
          projects yet.
        </Note>
      ) : (
        <Note lg>You declined the invitation. Nothing was shared with you.</Note>
      );
  } else if (!token) {
    body = (
      <Note tone="warn" lg>
        This invitation link is missing or incomplete. Open the link from your invitation email again, or ask the
        workspace owner to resend it.
      </Note>
    );
  } else if (loading) {
    body = spinner('Checking sign-in');
  } else if (!user) {
    body = (
      <>
        <p className="inv-lead">Sign in to see and respond to this workspace invitation.</p>
        <p className="inv-sub">Use Google with the email address the invitation was sent to.</p>
        <div className="inv-actions">
          <button type="button" className="inv-btn primary" onClick={signIn}>
            Sign in to continue
            <ArrowRight className="go" aria-hidden />
          </button>
        </div>
      </>
    );
  } else if (preview.isLoading) {
    body = spinner('Loading invitation');
  } else if (previewErr || preview.isError) {
    body =
      previewErr?.code === 'INVITATION_NOT_FOUND' ? (
        <Note tone="bad" lg>
          This invitation link is not valid. It may have been replaced by a newer email — if the owner resent the
          invitation, use the most recent link — or the invitation was removed.
        </Note>
      ) : (
        <Note
          tone="bad"
          lg
          action={
            <button type="button" className="act" onClick={() => void preview.refetch()}>
              Retry
            </button>
          }
        >
          The invitation could not be loaded. Try again in a moment.
        </Note>
      );
  } else if (p) {
    const expires = new Date(p.expires_at).toLocaleString();
    body = (
      <>
        <p className="inv-lead">
          <b>{p.inviter_email || 'The workspace owner'}</b> invited you to the workspace{' '}
          <b className="ws">{p.workspace_name || 'Untitled workspace'}</b>.
        </p>
        <dl className="inv-rows">
          <DetailRow icon={<ShieldCheck />} label="Role">
            <span className="inv-chip">{roleLabel(p.role)}</span>
          </DetailRow>
          <DetailRow icon={<Send />} label="Sent to">
            {p.invitee_email}
          </DetailRow>
          <DetailRow icon={<Activity />} label="Status">
            <span className={`inv-pill ${statusTone(p.status)}`} data-testid="invite-status">
              {p.status}
            </span>
          </DetailRow>
          <DetailRow icon={<Clock />} label="Expires">
            {expires}
          </DetailRow>
        </dl>

        {p.status === 'expired' ? (
          <Note tone="bad" icon={<Clock aria-hidden />}>
            This invitation has expired. Ask the workspace owner to invite you again.
          </Note>
        ) : p.status === 'revoked' ? (
          <Note tone="bad">The workspace owner withdrew this invitation.</Note>
        ) : p.status === 'accepted' ? (
          <Note>This invitation has already been accepted.</Note>
        ) : p.status === 'declined' ? (
          <Note tone="bad">This invitation was declined.</Note>
        ) : (
          <>
            {wrongEmail ? (
              <Note
                tone="warn"
                action={
                  <button type="button" className="act" onClick={() => void switchAccount()}>
                    Switch account
                  </button>
                }
              >
                You are signed in as {user.email}, but this invitation was sent to {p.invitee_email}. Sign in with the
                invited Google account to accept.
              </Note>
            ) : notGoogle ? (
              <Note
                tone="warn"
                action={
                  <button type="button" className="act" onClick={() => void switchAccount()}>
                    Sign in with Google
                  </button>
                }
              >
                Accepting requires a Google sign-in as {p.invitee_email}.
              </Note>
            ) : null}
            {respondErr ? <Note tone="bad">{respondErr.message}</Note> : null}
            {confirmDecline ? (
              <div className="inv-actions">
                <span className="q">Decline this invitation?</span>
                <button
                  type="button"
                  className="inv-btn danger"
                  disabled={respond.isPending}
                  onClick={() => void act('decline')}
                >
                  Decline Invitation
                </button>
                <button
                  type="button"
                  className="inv-btn"
                  disabled={respond.isPending}
                  onClick={() => setConfirmDecline(false)}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="inv-actions">
                <button
                  type="button"
                  className="inv-btn primary"
                  disabled={respond.isPending || wrongEmail || notGoogle}
                  onClick={() => void act('accept')}
                >
                  <Check aria-hidden />
                  Accept Invitation
                </button>
                <button
                  type="button"
                  className="inv-btn"
                  disabled={respond.isPending}
                  onClick={() => setConfirmDecline(true)}
                >
                  Decline Invitation
                </button>
              </div>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <div className="inv-page">
      <main className="inv-card" aria-live="polite">
        <div className="inv-brand">
          <span className="inv-tile" aria-hidden>
            <Users />
          </span>
          <div>
            <div className="inv-eyebrow">EnsoMirai++</div>
            <h1>Workspace invitation</h1>
          </div>
        </div>
        {body}
        {outcome || !token ? (
          <div className="inv-actions">
            <button type="button" className="inv-btn" onClick={() => navigate(user ? '/projects/all' : '/login')}>
              {user ? 'Go to projects' : 'Go to sign in'}
            </button>
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default InvitePage;
